// Safe duplicate detection + controlled merge helpers for the contact database.
// Pure functions (no I/O) so they can be unit-tested. The UI performs the actual
// writes only on explicit user confirmation — nothing is merged/deleted automatically.

export function normName(s) {
  return (s || '').toString().trim().toLowerCase().replace(/\s+/g, ' ')
}

// Identity of a single contact within a company (for de-duplicating the contacts array).
export function contactKey(c) {
  const email = (c?.email || '').trim().toLowerCase()
  const phone = (c?.phone || '').replace(/\D/g, '')
  const name = (c?.name || '').trim().toLowerCase()
  return email || phone || name || ''
}

// Remove duplicate contacts within one array (keeps first occurrence).
export function dedupeContactsArray(contacts = []) {
  const seen = new Set()
  const out = []
  for (const c of contacts) {
    const k = contactKey(c)
    if (k && seen.has(k)) continue
    if (k) seen.add(k)
    out.push(c)
  }
  return out
}

// Does a single company row have duplicate contacts inside it?
export function hasInternalDupes(row) {
  const contacts = row?.contacts || []
  return dedupeContactsArray(contacts).length < contacts.length
}

// Find groups of company rows that share the same normalized name (potential duplicates).
// Only groups with >1 row are returned. Each group: { name, key, rows }.
export function findCompanyDuplicateGroups(rows = []) {
  const map = new Map()
  for (const r of rows) {
    const key = normName(r.company)
    if (!key) continue
    if (!map.has(key)) map.set(key, [])
    map.get(key).push(r)
  }
  const groups = []
  for (const [key, groupRows] of map.entries()) {
    if (groupRows.length > 1) {
      groups.push({ key, name: groupRows[0].company, rows: groupRows })
    }
  }
  // Most duplicates first.
  return groups.sort((a, b) => b.rows.length - a.rows.length)
}

// Merge several company rows into the chosen keeper. Returns the merged field set
// for the keeper (contacts combined + de-duplicated; first non-empty wins for scalars).
// Does NOT mutate inputs and does NOT decide deletion — caller handles persistence.
export function planCompanyMerge(rows, keeperId) {
  const keeper = rows.find(r => r.id === keeperId) || rows[0]
  const others = rows.filter(r => r.id !== keeper.id)

  const mergedContacts = dedupeContactsArray(
    rows.flatMap(r => r.contacts || [])
  )
  const pick = (field) => rows.map(r => r[field]).find(v => v && String(v).trim()) || keeper[field] || ''

  return {
    keeperId: keeper.id,
    deleteIds: others.map(o => o.id),
    update: {
      contacts: mergedContacts,
      industry: pick('industry'),
      status: keeper.status,          // keep the keeper's status
      agent: keeper.agent,            // keep the keeper's owner
      source: keeper.source,
      event_name: pick('event_name'),
      notes: pick('notes'),
    },
    mergedContactsCount: mergedContacts.length,
  }
}
